window.dbs = window.dbs || {}
dbs.google = dbs.google || {}

dbs.google.placesService = function (google) {
  const self = this

  // Get API key from Google Maps script tag
  const getApiKey = () => {
    // Extract from Google Maps script tag
    const scripts = document.querySelectorAll('script[src*="maps.googleapis.com"]');
    for (const script of scripts) {
      const match = script.src.match(/key=([^&]+)/);
      if (match) {
        return match[1];
      }
    }
    
    console.error('Google Maps API key not found in script tag. Please ensure Google Maps script is loaded.');
    return null;
  };

  // Map legacy field names to new API field names (snake_case to camelCase)
  const mapLegacyFields = (fields) => {
    const fieldMapping = {
      'adr_address': 'adrFormatAddress',
      'formatted_address': 'formattedAddress',
      'geometry': 'location',
      'geometry.location': 'location',
      'place_id': 'id',
      'name': 'displayName',
      'plus_code': 'plusCode',
      'business_status': 'businessStatus',
      'opening_hours': 'currentOpeningHours',
      'permanently_closed': 'businessStatus', // Will need special handling
      'user_ratings_total': 'userRatingCount',
      'formatted_phone_number': 'nationalPhoneNumber',
      'international_phone_number': 'internationalPhoneNumber',
      'price_level': 'priceLevel',
      'website': 'websiteUri',
      'types': 'types',
      'photos': 'photos',
      'reviews': 'reviews',
      'rating': 'rating'
    };

    if (!fields || fields.length === 0) {
      // If no fields specified, return basic fields for cost optimization
      return ['id', 'displayName', 'formattedAddress', 'location'];
    }

    return fields.map(field => fieldMapping[field] || field);
  };

  /**
   * Retrieves details about the place identified by the given `placeId`.
   * Now uses the new Place Details API endpoint while maintaining the same interface.
   * See: https://developers.google.com/maps/documentation/places/web-service/details
   * @function
   * @param {object} request - Legacy PlaceDetailsRequest format that will be converted to new API format
   * @param {function} callback - callback: function(PlaceResult optional, PlacesServiceStatus): void
   */
  self.getDetails = function (request, callback) {
    const apiKey = getApiKey();
    if (!apiKey) {
      callback(null, 'REQUEST_DENIED');
      return;
    }

    if (!request.placeId) {
      callback(null, 'INVALID_REQUEST');
      return;
    }

    // Convert legacy fields to new API field names and create field mask
    const newApiFields = mapLegacyFields(request.fields);
    const fieldMask = newApiFields.join(','); // No 'places.' prefix needed for field mask

    // Prepare the API URL with properly encoded place ID
    let url = `https://places.googleapis.com/v1/places/${encodeURIComponent(request.placeId)}`;
    const params = new URLSearchParams();

    // Add session token if provided
    if (request.sessionToken) {
      if (typeof request.sessionToken === 'object' && request.sessionToken.toString) {
        params.append('sessionToken', request.sessionToken.toString());
      } else {
        params.append('sessionToken', request.sessionToken);
      }
    }

    // Add language code if provided
    if (request.language) {
      params.append('languageCode', request.language);
    }

    // Add region code if provided
    if (request.region) {
      params.append('regionCode', request.region);
    }

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    // Make the REST API call to new Places API
    fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': apiKey,
        'X-Goog-FieldMask': fieldMask
      }
          })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return response.json();
    })
    .then(data => {
      // Convert new API response format to legacy format for backward compatibility
      const legacyPlace = {
        place_id: data.id || request.placeId,
        name: data.displayName?.text || data.displayName || '',
        formatted_address: data.formattedAddress || '',
        adr_address: data.adrFormatAddress || '',
        types: data.types || [],
        url: data.googleMapsUri || '',
        website: data.websiteUri || '',
        business_status: data.businessStatus || 'OPERATIONAL',
        rating: data.rating || null,
        user_ratings_total: data.userRatingCount || 0,
        price_level: data.priceLevel || null,
        formatted_phone_number: data.nationalPhoneNumber || '',
        international_phone_number: data.internationalPhoneNumber || '',
        photos: (data.photos || []).map(photo => ({
          photo_reference: photo.name,
          height: photo.heightPx || 400,
          width: photo.widthPx || 400,
          html_attributions: photo.authorAttributions?.map(attr => attr.displayName) || [],
          getUrl: function(opts = {}) {
            const maxHeight = opts.maxHeight || opts.maxheight || 400;
            const maxWidth = opts.maxWidth || opts.maxwidth || 400;
            return `https://places.googleapis.com/v1/${photo.name}/media?maxHeightPx=${maxHeight}&maxWidthPx=${maxWidth}&key=${apiKey}`;
          }
        })),
        reviews: (data.reviews || []).map(review => ({
          author_name: review.authorAttribution?.displayName || '',
          author_url: review.authorAttribution?.uri || '',
          language: review.originalText?.languageCode || '',
          profile_photo_url: review.authorAttribution?.photoUri || '',
          rating: review.rating || 0,
          relative_time_description: review.relativePublishTimeDescription || '',
          text: review.originalText?.text || review.text?.text || '',
          time: Math.floor(new Date(review.publishTime).getTime() / 1000) || 0
        })),
        // Handle geometry/location conversion
        geometry: data.location ? {
          location: {
            lat: () => data.location.latitude,
            lng: () => data.location.longitude
          },
          viewport: data.viewport ? {
            northeast: {
              lat: () => data.viewport.high?.latitude || data.location.latitude + 0.01,
              lng: () => data.viewport.high?.longitude || data.location.longitude + 0.01
            },
            southwest: {
              lat: () => data.viewport.low?.latitude || data.location.latitude - 0.01,
              lng: () => data.viewport.low?.longitude || data.location.longitude - 0.01
            }
          } : null
        } : null,
        opening_hours: data.currentOpeningHours ? {
          open_now: data.currentOpeningHours.openNow || false,
          periods: data.currentOpeningHours.periods || [],
          weekday_text: data.currentOpeningHours.weekdayDescriptions || []
        } : null,
        plus_code: data.plusCode ? {
          compound_code: data.plusCode.compoundCode || '',
          global_code: data.plusCode.globalCode || ''
        } : null
      };

      // Handle legacy 'permanently_closed' mapping
      if (data.businessStatus === 'CLOSED_PERMANENTLY') {
        legacyPlace.permanently_closed = true;
      }

      // Simulate legacy PlacesServiceStatus.OK
      callback(legacyPlace, 'OK');
    })
    .catch(error => {
      console.error('Place Details API error:', error);
      
      // Map different error types to legacy status codes
      let status = 'UNKNOWN_ERROR';
      if (error.message.includes('403')) {
        status = 'REQUEST_DENIED';
      } else if (error.message.includes('400')) {
        status = 'INVALID_REQUEST';
      } else if (error.message.includes('404')) {
        status = 'NOT_FOUND';
      } else if (error.message.includes('429')) {
        status = 'OVER_QUERY_LIMIT';
      }
      
      callback(null, status);
    });
  }
}
